import type { Segment } from '@/lib/types'
import type { EvidenceRow } from '@/lib/ensemble/types'

// Deterministic seed — always same demo data
export const DEMO_PROJECT_ID = 'proj_hc_reminder_demo'
export const DEMO_PANEL_ID = 'panel_hc_reminder_demo'
export const DEMO_TENANT_ID = 'tnt_demo'
export const DEMO_WORKSPACE_ID = 'wks_demo'

export const DEMO_PRE_READ_QUESTION = 'Which appointment reminder message drives the highest adherence across literacy levels?'

export const DEMO_STIMULI: Array<{ stimulus_id: string; label: string; content: string; type: string }> = [
  {
    stimulus_id: 'stim_a',
    label: 'Message A',
    content: 'This is a reminder that you have a scheduled appointment with your primary care provider on Thursday, March 14th at 10:30 AM at 4820 Westfield Medical Plaza, Suite 210. Please arrive 15 minutes early and bring a valid photo ID and insurance card. To reschedule, contact our scheduling department at (800) 555-0182.',
    type: 'message',
  },
  {
    stimulus_id: 'stim_b',
    label: 'Message B',
    content: 'Hi! You have a doctor\'s appointment on Thursday, March 14 at 10:30 AM. Reply YES to confirm or CANCEL to cancel. Need to reschedule? Call (800) 555-0182.',
    type: 'message',
  },
  {
    stimulus_id: 'stim_c',
    label: 'Message C',
    content: 'Appointment reminder: Thursday March 14, 10:30 AM. Reply YES to confirm or HELP for assistance.',
    type: 'message',
  },
]

export const DEMO_SEGMENTS: Segment[] = [
  {
    segment_id: 'seg_standard_literacy',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_hc_reminder_demo',
    name: 'Standard Literacy Members',
    respondent_count: 720,
    demographic_skew: { age_median: 41, education: 'high school or higher', insurance_type: 'commercial' },
    dominant_themes: [
      'Clear confirm/cancel reply option reduces friction',
      'Brevity preferred — full address adds unnecessary length',
      'Interactive reply builds a sense of control',
    ],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'I just want to tap YES and be done. Message C is the fastest. I don\'t need the address in a text.', segment: 'Standard Literacy Members', source_file: 'appointment-reminder-survey.csv', source_row: 58 },
      { text: 'Message B is good because I can cancel from the same text. I don\'t have to hunt for a number.', segment: 'Standard Literacy Members', source_file: 'appointment-reminder-survey.csv', source_row: 124 },
      { text: 'Message A has too much in it. By the time I get to the phone number I\'ve already forgotten the date.', segment: 'Standard Literacy Members', source_file: 'appointment-reminder-survey.csv', source_row: 213 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_low_literacy',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_hc_reminder_demo',
    name: 'Low-Literacy Members',
    respondent_count: 214,
    demographic_skew: { age_median: 46, education: 'less than high school diploma', insurance_type: 'Medicaid' },
    dominant_themes: [
      'Long messages cause confusion and go unread',
      'Simple YES/HELP reply options reduce anxiety about responding',
      'Confusion with Message A leads to missed appointments',
    ],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'I read Message C and I knew exactly what to do. The other ones felt like homework.', segment: 'Low-Literacy Members', source_file: 'appointment-reminder-survey.csv', source_row: 498 },
      { text: 'Message A is too long. I stopped reading halfway through and almost missed the date.', segment: 'Low-Literacy Members', source_file: 'appointment-reminder-survey.csv', source_row: 547 },
      { text: 'HELP feels safer than CANCEL. I don\'t want to accidentally cancel my appointment.', segment: 'Low-Literacy Members', source_file: 'appointment-reminder-survey.csv', source_row: 601 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_elderly',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_hc_reminder_demo',
    name: 'Elderly Members 65+',
    respondent_count: 156,
    demographic_skew: { age_median: 72, mobile_usage: 'basic SMS only', insurance_type: 'Medicare' },
    dominant_themes: [
      'Clinic name and phone number essential for trust',
      'Terse messages feel impersonal and create uncertainty',
      'Prefer full address to verify appointment location',
    ],
    evidence_strength: 'Medium',
    representative_quotes: [
      { text: 'Message C doesn\'t even tell me which clinic. I have three different doctors. How do I know where to go?', segment: 'Elderly Members 65+', source_file: 'appointment-reminder-survey.csv', source_row: 812 },
      { text: 'I need the phone number right there in the message. Message A has it. The others make me go digging.', segment: 'Elderly Members 65+', source_file: 'appointment-reminder-survey.csv', source_row: 867 },
      { text: 'I appreciate Message A telling me to bring my insurance card. The short ones assume I know things I sometimes forget.', segment: 'Elderly Members 65+', source_file: 'appointment-reminder-survey.csv', source_row: 904 },
    ],
    created_at: 1700000000000,
  },
]

export const DEMO_EVIDENCE: EvidenceRow[] = [
  { respondent_id: 'R001', segment: 'Standard Literacy Members', response: 'Message C', open_text: 'I just want to tap YES and be done. Message C is the fastest. I don\'t need the address in a text.', source_file: 'appointment-reminder-survey.csv', row_index: 58 },
  { respondent_id: 'R002', segment: 'Standard Literacy Members', response: 'Message B', open_text: 'Message B is good because I can cancel from the same text. I don\'t have to hunt for a number.', source_file: 'appointment-reminder-survey.csv', row_index: 124 },
  { respondent_id: 'R003', segment: 'Standard Literacy Members', response: 'Message A', open_text: 'Message A has too much in it. By the time I get to the phone number I\'ve already forgotten the date.', source_file: 'appointment-reminder-survey.csv', row_index: 213 },
  { respondent_id: 'R004', segment: 'Standard Literacy Members', response: 'Message C', open_text: 'Short is better. I get dozens of texts a day. If I have to scroll I\'m probably not going to read it.', source_file: 'appointment-reminder-survey.csv', row_index: 289 },
  { respondent_id: 'R005', segment: 'Low-Literacy Members', response: 'Message C', open_text: 'I read Message C and I knew exactly what to do. The other ones felt like homework.', source_file: 'appointment-reminder-survey.csv', row_index: 498 },
  { respondent_id: 'R006', segment: 'Low-Literacy Members', response: 'Message A', open_text: 'Message A is too long. I stopped reading halfway through and almost missed the date.', source_file: 'appointment-reminder-survey.csv', row_index: 547 },
  { respondent_id: 'R007', segment: 'Low-Literacy Members', response: 'Message C', open_text: 'HELP feels safer than CANCEL. I don\'t want to accidentally cancel my appointment.', source_file: 'appointment-reminder-survey.csv', row_index: 601 },
  { respondent_id: 'R008', segment: 'Low-Literacy Members', response: 'Message C', open_text: 'Two words and I know what to do. That is the right way to send me a reminder.', source_file: 'appointment-reminder-survey.csv', row_index: 648 },
  { respondent_id: 'R009', segment: 'Elderly Members 65+', response: 'Message A', open_text: 'Message C doesn\'t even tell me which clinic. I have three different doctors. How do I know where to go?', source_file: 'appointment-reminder-survey.csv', row_index: 812 },
  { respondent_id: 'R010', segment: 'Elderly Members 65+', response: 'Message A', open_text: 'I need the phone number right there in the message. Message A has it. The others make me go digging.', source_file: 'appointment-reminder-survey.csv', row_index: 867 },
  { respondent_id: 'R011', segment: 'Elderly Members 65+', response: 'Message A', open_text: 'I appreciate Message A telling me to bring my insurance card. The short ones assume I know things I sometimes forget.', source_file: 'appointment-reminder-survey.csv', row_index: 904 },
  { respondent_id: 'R012', segment: 'Standard Literacy Members', response: 'Message C', open_text: 'If the only thing I need to do is reply YES, then tell me that and nothing else. Message C nails it.', source_file: 'appointment-reminder-survey.csv', row_index: 352 },
]
